import { useState, useCallback, useRef, useEffect } from 'react';
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import { dynamicQuery, orgModulesByModuleQuery } from '../queries';
import {
  escapeRegex,
  resolveOrganizationObjectIds,
} from "../../../utils/orgSearch";
/**
 * Hook that provides dynamic filtering with a single GraphQL query
 * Builds the where clause dynamically based on active filters and search term
 * Supports independent logical operations (AND, OR) for each filter
 */
export const useDynamicFilter = (
  setLoading,
  setOrgs,
  setPagination,
  setFilteredOrgsCount
) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchObjectIds, setSearchObjectIds] = useState([]);
  const [activeFilters, setActiveFilters] = useState([]);
  const searchTimeoutRef = useRef(null);
  
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";  
  
  // Track current pagination state locally
  const [currentPagination, setCurrentPagination] = useState({
    current: 1,
    pageSize: 10
  });

  // Update local pagination when the parent state changes
  useEffect(() => {
    setPagination(prev => {
      setCurrentPagination({
        current: prev.current,
        pageSize: prev.pageSize
      });
      return prev;
    });
  }, [setPagination]);

  /**
   * Build a dynamic where clause based on search term and active filters
   * Each filter uses its own logical operator
   */
  const buildWhereClause = useCallback((params = {}) => {
    const searchTerm = params.searchTerm !== undefined
      ? params.searchTerm.trim()
      : searchValue.trim();
    const resolvedSearchObjectIds = params.searchObjectIds !== undefined
      ? params.searchObjectIds
      : searchObjectIds;
    const filters = params.filters || activeFilters;
    const whereClause = {};

    const searchConditions = [];
    if (searchTerm) {
      searchConditions.push({
        nome: { matchesRegex: escapeRegex(searchTerm), options: "i" },
      });
    }
    if (resolvedSearchObjectIds.length > 0) {
      searchConditions.push({
        objectId: { in: resolvedSearchObjectIds },
      });
    }

    const searchCondition = searchConditions.length === 0
      ? null
      : searchConditions.length === 1
        ? searchConditions[0]
        : { OR: searchConditions };

    if (filters && filters.length > 0) {
      // Debug each filter
      filters.forEach(filter => {
        console.log(`Processing filter: column=${filter.column}, operator=${filter.operator}, value=${filter.value}`);
      });
      
      // Group filters by logical operator
      const filtersByOperator = filters.reduce((acc, filter) => {
        const op = filter.logicalOperator || "AND";
        if (!acc[op]) acc[op] = [];
        
        const condition = {};
        // First check if it's an exists operator
        const operator = filter.operator || 'equalTo';
        
        if (operator === 'exists') {
          console.log(`Setting exists: ${filter.value} for field ${filter.column}`);
          condition[filter.column] = { exists: filter.value };
        }
        else if (operator === 'isNull') {
          // Use existence semantics for null checks
          condition[filter.column] = { exists: false };
        }
        else if (operator === 'isNotNull') {
          // Use existence semantics for not-null checks
          condition[filter.column] = { exists: true };
        }
        // Then handle other operators
        else if (typeof filter.value === 'boolean') {
          condition[filter.column] = { equalTo: filter.value };
        } else {
          // Use the selected operator from the filter
          let operatorConfig;
          
          // Special handling for text search on 'nome'
          if (filter.column === 'nome' && operator === 'contains') {
            operatorConfig = { matchesRegex: filter.value, options: 'i' };
          } else switch (operator) {
            case 'notEqualTo':
              operatorConfig = { notEqualTo: filter.value };
              break;
            case 'greaterThan':
              operatorConfig = { greaterThan: filter.value };
              break;
            case 'lessThan':
              operatorConfig = { lessThan: filter.value };
              break;
            case 'greaterThanOrEqualTo':
              operatorConfig = { greaterThanOrEqualTo: filter.value };
              break;
            case 'lessThanOrEqualTo':
              operatorConfig = { lessThanOrEqualTo: filter.value };
              break;
            case 'contains':
              operatorConfig = { includes: filter.value };
              break;
            case 'in':
              operatorConfig = { in: Array.isArray(filter.value) ? filter.value : [filter.value] };
              break;
            case 'notIn':
              operatorConfig = { notIn: Array.isArray(filter.value) ? filter.value : [filter.value] };
              break;
            case 'equalTo':
            default:
              operatorConfig = { equalTo: filter.value };
              break;
          }

          // Normalize null comparisons even if passed via equalTo/notEqualTo
          if (filter.value === null) {
            // Normalize null comparisons to existence semantics
            condition[filter.column] = { exists: operator === 'notEqualTo' ? true : false };
          } else {
            condition[filter.column] = operatorConfig;
          }
        }
        acc[op].push(condition);
        
        return acc;
      }, {});
      
      // Create the overall structure for the where clause
      const conditions = [];

      if (searchCondition) {
        conditions.push(searchCondition);
      }

      // Process AND conditions
      if (filtersByOperator.AND && filtersByOperator.AND.length > 0) {
        if (filtersByOperator.AND.length === 1 && conditions.length === 0) {
          // If there's only one AND condition and no search, apply it directly
          const condition = filtersByOperator.AND[0];
          const key = Object.keys(condition)[0];
          whereClause[key] = condition[key];
        } else {
          // Multiple AND conditions or combined with search
          conditions.push(...filtersByOperator.AND);
        }
      }
      
      // Process OR conditions
      if (filtersByOperator.OR && filtersByOperator.OR.length > 0) {
        if (filtersByOperator.OR.length === 1 && conditions.length === 0) {
          // If there's only one OR condition and no other conditions, apply it directly
          const condition = filtersByOperator.OR[0];
          const key = Object.keys(condition)[0];
          whereClause[key] = condition[key];
        } else if (filtersByOperator.OR.length > 1) {
          // Multiple OR conditions
          conditions.push({ OR: filtersByOperator.OR });
        } else {
          // Single OR condition combined with other conditions
          conditions.push(filtersByOperator.OR[0]);
        }
      }

      // Combine all conditions with AND if we have multiple conditions
      if (conditions.length > 1) {
        whereClause.AND = conditions;
      } else if (conditions.length === 1) {
        Object.assign(whereClause, conditions[0]);
      }
    } else if (searchCondition) {
      Object.assign(whereClause, searchCondition);
    }
    
    console.log('Final where clause:', JSON.stringify(whereClause, null, 2));
    return whereClause;
  }, [searchValue, searchObjectIds, activeFilters]);

  /**
   * Resolve module filters (two-step approach):
   * For each module filter, query org_modules to get the list of org IDs
   * that have (or don't have) the specified module enabled.
   * Returns { inIds, notInIds } to be injected into the main where clause.
   * Returns null if there are no module filters.
   * Returns { empty: true } if a "hasModule" filter resolves to zero orgs (short-circuit).
   */
  const resolveModuleFilters = useCallback(async (filters, token, graphqlUrl) => {
    const moduleFilters = (filters || []).filter(f => f.column === "__module__");
    if (moduleFilters.length === 0) return null;

    // Collect org IDs per "hasModule" filter (we'll intersect them)
    const hasModuleSets = [];
    const notHasModuleIds = new Set();

    for (const mf of moduleFilters) {
      const where = {
        module_pointer: { have: { objectId: { equalTo: mf.value } } },
        enabled: { equalTo: true },
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: orgModulesByModuleQuery,
          variables: { where },
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      const edges = response.data?.org_modules?.edges || [];
      const orgIds = edges
        .map(e => e.node?.org_pointer?.objectId)
        .filter(Boolean);

      if (mf.operator === "hasModule") {
        hasModuleSets.push(new Set(orgIds));
      } else if (mf.operator === "notHasModule") {
        orgIds.forEach(id => notHasModuleIds.add(id));
      }
    }

    // Intersect all "hasModule" sets
    let inIds = null;
    if (hasModuleSets.length > 0) {
      inIds = [...hasModuleSets[0]];
      for (let i = 1; i < hasModuleSets.length; i++) {
        inIds = inIds.filter(id => hasModuleSets[i].has(id));
      }
      // Short-circuit: if intersection is empty, no orgs match
      if (inIds.length === 0) {
        return { empty: true };
      }
    }

    // If we have both inIds and notInIds, remove notIn from inIds
    if (inIds && notHasModuleIds.size > 0) {
      inIds = inIds.filter(id => !notHasModuleIds.has(id));
      if (inIds.length === 0) {
        return { empty: true };
      }
    }

    return {
      inIds: inIds, // array or null
      notInIds: notHasModuleIds.size > 0 && !inIds ? [...notHasModuleIds] : null,
    };
  }, []);

  /**
   * Fetch data with the dynamic where clause
   */
  const fetchFilteredData = useCallback(async (params = {}) => {
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

    try {
      setLoading(true);
      const token = await getTokenSilently();
      const currentSearchTerm = params.searchTerm !== undefined
        ? params.searchTerm.trim()
        : searchValue.trim();

      // Separate module filters from normal filters
      const allFilters = params.filters || activeFilters;
      const normalFilters = allFilters.filter(f => f.column !== "__module__");

      // Resolve module filters (two-step)
      const moduleResult = await resolveModuleFilters(allFilters, token, graphqlUrl);

      // Short-circuit: if module filter resolved to zero orgs, show empty results
      if (moduleResult?.empty) {
        setOrgs([]);
        const currentPage = params.currentPage || currentPagination.current || 1;
        const pageSize = params.pageSize || currentPagination.pageSize || 10;
        setPagination(prev => ({
          current: currentPage,
          pageSize: pageSize,
          total: 0,
        }));
        setSearchObjectIds([]);
        setFilteredOrgsCount(0);
        setLoading(false);
        return;
      }

      const resolvedSearchObjectIds = currentSearchTerm
        ? await resolveOrganizationObjectIds({
            env,
            token,
            searchTerm: currentSearchTerm,
          })
        : [];

      setSearchObjectIds(resolvedSearchObjectIds);

      // Build the where clause using only normal filters
      const where = buildWhereClause({
        ...params,
        searchTerm: currentSearchTerm,
        searchObjectIds: resolvedSearchObjectIds,
        filters: normalFilters,
      });

      // Inject objectId constraints from module filters
      if (moduleResult?.inIds) {
        where.objectId = { ...(where.objectId || {}), in: moduleResult.inIds };
      }
      if (moduleResult?.notInIds) {
        where.objectId = { ...(where.objectId || {}), notIn: moduleResult.notInIds };
      }

      // Determine sort order
      let order = "createdAt_DESC"; // default order
      if (params.sortField === "nome") {
        order = params.sortOrder === "ascend" ? "nome_ASC" : "nome_DESC";
      }

      // Calculate skip based on page and pageSize
      const currentPage = params.currentPage || currentPagination.current || 1;
      const pageSize = params.pageSize || currentPagination.pageSize || 10;
      const skip = (currentPage - 1) * pageSize;

      // Set up variables
      const variables = {
        first: pageSize,
        skip: skip,
        where: where,
        order: order
      };

      console.log('Executing query with variables:', JSON.stringify(variables, null, 2));

      // Execute the query
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query: dynamicQuery,
          variables: variables
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.organizzaziones) {
        const { edges, count } = response.data.organizzaziones;

        const results = edges.map(edge => edge.node);
        setOrgs(results);

        // Update pagination and filtered count
        const totalResults = count || results.length;
        setPagination(prev => ({
          // Preserve the current page if it's passed in params, otherwise keep the previous value
          current: currentPage,
          pageSize: pageSize,
          total: totalResults,
        }));

        setFilteredOrgsCount(totalResults);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error in dynamic filter:", error);
      console.error("Error details:", error.response?.data || error.message);
      setLoading(false);
    }
  }, [setLoading, getTokenSilently, searchValue, buildWhereClause, resolveModuleFilters, activeFilters, currentPagination, env, setOrgs, setPagination, setFilteredOrgsCount]);

  /**
   * Handle search input changes with debounce
   */
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    
    // Immediately update the search value state to ensure it's updated before any query runs
    setSearchValue(value);
    setSearchObjectIds([]);
    
    // Reset pagination to page 1 when search term changes
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set a new timeout for debouncing
    searchTimeoutRef.current = setTimeout(() => {
      // When the value is empty (cleared), explicitly pass empty string to ensure it overrides previous values
      fetchFilteredData({ 
        searchTerm: value, 
        currentPage: 1 
      });
    }, 300);
  }, [fetchFilteredData, setPagination]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((newFilters, skipQuery = false) => {
    console.log('handleFilterChange called with', newFilters.length, 'filters, skipQuery =', skipQuery);
    console.log('Filter details:', JSON.stringify(newFilters, null, 2));
    
    // Only update state without triggering a query
    setActiveFilters(newFilters);
    
    // Reset pagination to page 1 when filters change
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    // Skip query if specified (e.g. when change comes from URL)
    if (skipQuery) {
      console.log('Skipping query execution because skipQuery = true');
      return;
    }
    
    // Use a timeout to batch filter changes and prevent multiple queries
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Executing query after timeout with', newFilters.length, 'filters');
      fetchFilteredData({ filters: newFilters, currentPage: 1 }); // Force page 1 when filters change
    }, 50); // Short timeout to batch changes
  }, [fetchFilteredData, setPagination]);

  /**
   * Clear all filters and search
   */
  const clearAll = useCallback(() => {
    setSearchValue("");
    setSearchObjectIds([]);
    setActiveFilters([]);
    
    // Reset pagination to page 1 when clearing filters
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    fetchFilteredData({ searchTerm: "", filters: [], currentPage: 1 });
  }, [fetchFilteredData, setPagination]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    searchValue,
    setSearchValue,
    activeFilters,

    // Functions
    fetchFilteredData,
    handleSearchChange,
    handleFilterChange,
    clearAll,

    // Expose current where clause builder so other components can reuse the same filtering logic
    buildWhereClause
  };
};

export default useDynamicFilter; 
